import React, { lazy, Suspense } from 'react';
import { SvgIcon } from '@mui/material';

// Función para importar dinámicamente iconos de Material UI
const createLazyIcon = (iconName: string) => {
  try {
    return lazy(() =>
      import('@mui/icons-material').then((module) => {
        const IconComponent = module[iconName as keyof typeof module] as React.ComponentType<any>;
        if (!IconComponent) {
          // Si no se encuentra el icono, retornar un icono por defecto
          return { default: module.HelpOutline as React.ComponentType<any> };
        }
        return { default: IconComponent };
      }).catch(() => {
        // En caso de error, importar icono por defecto
        return import('@mui/icons-material').then((module) => ({
          default: module.HelpOutline as React.ComponentType<any>
        }));
      })
    );
  } catch (error) {
    // Fallback en caso de error
    return lazy(() => import('@mui/icons-material').then((module) => ({
      default: module.HelpOutline as React.ComponentType<any>
    })));
  }
};

// Componente que renderiza un icono de Material UI dinámicamente
interface DynamicMUIIconProps {
  iconName: string;
  className?: string;
  style?: React.CSSProperties;
  fontSize?: 'inherit' | 'large' | 'medium' | 'small';
  color?: 'inherit' | 'action' | 'disabled' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

export const DynamicMUIIcon: React.FC<DynamicMUIIconProps> = ({ 
  iconName, 
  className, 
  style, 
  fontSize = 'medium',
  color = 'inherit',
  ...props 
}) => {
  if (!iconName) {
    // Si no hay nombre de icono, mostrar icono por defecto
    return <SvgIcon fontSize={fontSize} color={color} className={className} style={style} {...props} />;
  }

  const LazyIcon = createLazyIcon(iconName);

  return (
    <Suspense 
      fallback={
        <SvgIcon 
          fontSize={fontSize} 
          color={color} 
          className={className} 
          style={style} 
          {...props}
        />
      }
    >
      <LazyIcon 
        fontSize={fontSize} 
        color={color} 
        className={className} 
        style={style} 
        {...props} 
      />
    </Suspense>
  );
};
